// @flow
import * as React from 'react';
import useVisibleEffect from './useVisibleEffect';

type OnVisible = () => mixed;

/** Hook wrapper around the intersection observer API. Will fire a callback exactly once when visibility transitions to true
 * @type T The HTMLElement type to attach to
 *
 * @param onVisible The callback to fire at most one time
 * @param rootMargin See useVisibleEffect.jsx
 * @param threshold See useVisibleEffect.jsx
 * @returns {{current: (T|null)}} See useVisibleEffect.jsx
 */
const useOnVisible = <T>(
  onVisible: OnVisible | null,
  rootMargin?: number | string,
  threshold?: number,
): { current: T | null } => {
  const hasFiredCallback = React.useRef(false);
  // if we've fired the callback, then change onChange to null so the useVisibleEffect hook no longer creates an intersection observer
  const onChange = hasFiredCallback.current
    ? null
    : visible => {
        if (!hasFiredCallback.current && visible) {
          if (onVisible) {
            onVisible();
          }
          hasFiredCallback.current = true;
        }
      };
  return useVisibleEffect(onChange, rootMargin, threshold);
};

export default useOnVisible;
